import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { ServiceContext, type ServiceContextValue } from '@/contexts/ServiceContext';
import { useBusiness } from '@/hooks/useBusiness';
import { useDataStore } from '@/hooks/useDataStore';
import type { CreateInput, ID, Service, ServiceCategory, UpdateInput } from '@/types';

const NO_CATEGORIES: ServiceCategory[] = [];
const NO_SERVICES: Service[] = [];

export function ServiceProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const { activeBusiness } = useBusiness();
  const businessId = activeBusiness?.id ?? null;

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** The business the catalog in state was loaded for — see `current` below. */
  const [loadedFor, setLoadedFor] = useState<ID | null>(null);

  useEffect(() => {
    if (!businessId) {
      setCategories([]);
      setServices([]);
      setLoadedFor(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void Promise.all([store.categories.list(), store.services.list()]).then(
      ([allCategories, allServices]) => {
        if (cancelled) return;
        setCategories(
          allCategories
            .filter((category) => category.businessId === businessId)
            .sort((a, b) => a.order - b.order),
        );
        setServices(allServices.filter((service) => service.businessId === businessId));
        setLoadedFor(businessId);
        setIsLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [store, businessId]);

  const requireBusiness = useCallback((): ID => {
    if (!businessId) throw new Error('No active business — catalog operations are unavailable.');
    return businessId;
  }, [businessId]);

  const createCategory = useCallback<ServiceContextValue['createCategory']>(
    async (input) => {
      const created = await store.categories.create({
        ...input,
        businessId: requireBusiness(),
      } as CreateInput<ServiceCategory>);
      setCategories((current) => [...current, created].sort((a, b) => a.order - b.order));
      return created;
    },
    [store, requireBusiness],
  );

  const updateCategory = useCallback(
    async (id: ID, patch: UpdateInput<ServiceCategory>) => {
      const next = await store.categories.update(id, patch);
      setCategories((current) =>
        current.map((category) => (category.id === id ? next : category)).sort((a, b) => a.order - b.order),
      );
    },
    [store],
  );

  const deleteCategory = useCallback(
    async (id: ID) => {
      await store.services.removeWhere((service) => service.categoryId === id);
      await store.categories.remove(id);
      setServices((current) => current.filter((service) => service.categoryId !== id));
      setCategories((current) => current.filter((category) => category.id !== id));
    },
    [store],
  );

  const createService = useCallback<ServiceContextValue['createService']>(
    async (input) => {
      const created = await store.services.create({
        ...input,
        businessId: requireBusiness(),
      } as CreateInput<Service>);
      setServices((current) => [...current, created]);
      return created;
    },
    [store, requireBusiness],
  );

  const updateService = useCallback(
    async (id: ID, patch: UpdateInput<Service>) => {
      const next = await store.services.update(id, patch);
      setServices((current) => current.map((service) => (service.id === id ? next : service)));
    },
    [store],
  );

  const duplicateService = useCallback(
    async (id: ID) => {
      const source = services.find((service) => service.id === id);
      if (!source) return;
      const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...rest } = source;
      const created = await store.services.create({
        ...rest,
        name: `${source.name} (copy)`,
        usageCount: 0,
      } as CreateInput<Service>);
      setServices((current) => [...current, created]);
    },
    [store, services],
  );

  const deleteService = useCallback(
    async (id: ID) => {
      await store.services.remove(id);
      setServices((current) => current.filter((service) => service.id !== id));
    },
    [store],
  );

  const toggleFavourite = useCallback(
    async (id: ID) => {
      const target = services.find((service) => service.id === id);
      if (!target) return;
      await updateService(id, { isFavourite: !target.isFavourite });
    },
    [services, updateService],
  );

  const setStatus = useCallback(
    async (id: ID, status: Service['status']) => updateService(id, { status }),
    [updateService],
  );

  // Right after a business switch the previous catalog is still in state until
  // the reload lands; it is never exposed.
  const current = loadedFor === businessId;

  const value = useMemo<ServiceContextValue>(
    () => ({
      categories: current ? categories : NO_CATEGORIES,
      services: current ? services : NO_SERVICES,
      isLoading: isLoading || !current,
      createCategory,
      updateCategory,
      deleteCategory,
      createService,
      updateService,
      duplicateService,
      deleteService,
      toggleFavourite,
      setStatus,
    }),
    [
      categories, services, isLoading, current, createCategory, updateCategory, deleteCategory,
      createService, updateService, duplicateService, deleteService, toggleFavourite, setStatus,
    ],
  );

  return <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>;
}
